import * as THREE from "three";
import { activeStyle, type MaterialParams, type Surface } from "./styles";
import { splice } from "./shader";

// The shared surface material and ink outlines, both from the active art
// style (#75, `render/styles/`). The cartoon style is the "plastic toy" look.

export type ToyParams = MaterialParams;

/**
 * The shared surface material (named for the old toon shader it replaced):
 * whatever the active style makes of a `surface` in this color. Pass
 * roughness / metalness / maps to vary it.
 */
export function toon(color: THREE.ColorRepresentation, extra: ToyParams = {}, surface: Surface = "plastic"): THREE.MeshStandardMaterial {
  return activeStyle().material(color, extra, surface);
}

const outlineCache = new Map<string, THREE.MeshBasicMaterial>();

/** Back-face material pushed out along normals: draws a clean ink line around a mesh. */
function outlineMaterial(thickness: number, ink: THREE.ColorRepresentation): THREE.MeshBasicMaterial {
  const key = `${thickness}:${new THREE.Color(ink).getHexString()}`;
  const cached = outlineCache.get(key);
  if (cached) return cached;
  const mat = new THREE.MeshBasicMaterial({ color: ink, side: THREE.BackSide });
  mat.onBeforeCompile = (shader) => {
    shader.vertexShader = splice(shader.vertexShader, "#include <begin_vertex>", `transformed += normalize(normal) * ${thickness.toFixed(4)};`);
  };
  mat.customProgramCacheKey = () => `outline-${thickness}`;
  outlineCache.set(key, mat);
  return mat;
}

/** Creates a mesh with a thin ink outline as a child (none when the style draws no outlines). */
export function inked(
  geometry: THREE.BufferGeometry,
  material: THREE.Material | THREE.Material[],
  thickness = 0.018,
): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = true;
  const { scale, ink } = activeStyle().outlines;
  if (scale > 0) {
    const outline = new THREE.Mesh(geometry, outlineMaterial(thickness * scale, ink));
    outline.castShadow = false;
    outline.name = "outline";
    mesh.add(outline);
  }
  return mesh;
}
